// components/ProfileMenu.tsx
import React, { useState } from 'react';
import { Badge, List } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

// Icons
import AccountBoxIcon from '@mui/icons-material/AccountBox';
import ChatIcon from '@mui/icons-material/Chat';
import DeleteIcon from '@mui/icons-material/Delete';
import FavoriteIcon from '@mui/icons-material/Favorite';
import LockResetIcon from '@mui/icons-material/LockReset';
import LogoutIcon from '@mui/icons-material/Logout';
import InboxIcon from '@mui/icons-material/MoveToInbox';
import NotificationsIcon from '@mui/icons-material/Notifications';
import StarsIcon from '@mui/icons-material/Stars';
import CreditCardIcon from '@mui/icons-material/CreditCard';

// Components and hooks
import ProfileMenuItem from './ProfileMenuItem';
import DeleteAccountDialog from './DeleteAccountDialog';
import { useLogout } from '../hooks/useLogout';

// Utils and types
import { getIconStyle, ICON_COLORS } from '../utils/iconStyles';
import { ProfilePopupMenuProps } from '../types/ProfileMenu.types';
import { useAppSelector } from '../redux/hooks';
import { selectUser, selectUserRole } from '../redux/slices/user';
import { postData } from '../redux/thunks/itemsApi';
import { Endpoints } from '../api';
import ShoppingBagIcon from '@mui/icons-material/ShoppingBag';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import CampaignIcon from '@mui/icons-material/Campaign';
import { selectCartCount } from '../redux/slices/cart';
import { featureFlags } from '../config/featureFlags';

const ProfileMenu: React.FC<ProfilePopupMenuProps> = ({ hasDivider = false, handleClose }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const userInfo = useAppSelector(selectUser);
    const userRole = useAppSelector(selectUserRole);
    const count = useAppSelector(selectCartCount);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);

    const { handleLogout } = useLogout(handleClose);

    const navigateAndClose = (path: string) => {
        navigate(path);
        handleClose();
    };

    const deleteAccount = async (email: string) => {
        try {
            await postData(`${Endpoints.baseUrl}/Account/DeleteUser`, { email });
            handleLogout();
        } catch (error) {
            console.error('Failed to delete account:', error);
            // Handle error appropriately
        }
    };

    const canSeeAdminBannerManagement = !featureFlags.adminBannerModeration || userRole === 'admin';

    const menuItems = [
        {
            icon: <ChatIcon sx={getIconStyle(ICON_COLORS.ORANGE)} />,
            label: t('profile_menu.chat', 'Chat'),
            onClick: () => navigateAndClose('/Chat')
        },
        {
            icon: <AccountBoxIcon sx={getIconStyle(ICON_COLORS.GREEN)} />,
            label: t('profile_menu.my_profile', 'My Profile'),
            onClick: () => navigateAndClose('/myprofile')
        },
        {
            icon: <FavoriteIcon sx={getIconStyle(ICON_COLORS.RED)} />,
            label: t('profile_menu.saved', 'Saved'),
            onClick: () => navigateAndClose('/MyLikes')
        },
        {
            icon: <InboxIcon sx={getIconStyle(ICON_COLORS.BLUE)} />,
            label: t('profile_menu.my_products', 'My Products'),
            onClick: () => navigateAndClose('/MyProducts')
        },
        {
            icon: <StarsIcon sx={getIconStyle(ICON_COLORS.YELLOW)} />,
            label: t('profile_menu.rates', 'Rates'),
            onClick: () => navigateAndClose('/MyRate')
        },
        {
            icon: <CreditCardIcon sx={getIconStyle(ICON_COLORS.BLUE)} />,
            label: t('profile_menu.my_cards', 'My Cards'),
            onClick: () => navigateAndClose('/mycards')
        },
        // TODO: Add notifications routing when backend is ready
        // {
        //   icon: <NotificationsIcon sx={getIconStyle(ICON_COLORS.DARK_RED)} />,
        //   label: t('profile_menu.notifications', 'Notifications'),
        //   onClick: () => navigateAndClose('/notifications')
        // },
        ...(featureFlags.showCartButton
            ? [
                  {
                      icon: (
                          <Badge
                              badgeContent={count}
                              color="secondary"
                              sx={{
                                  '& .MuiBadge-badge': {
                                      fontSize: 12,
                                      bgcolor: '#FECE00',
                                      color: '#fff'
                                  }
                              }}
                          >
                              <ShoppingCartIcon sx={getIconStyle(ICON_COLORS.GREEN)} />
                          </Badge>
                      ),
                      label: t('web.cart', 'Cart'),
                      onClick: () => navigateAndClose('/cart'),
                      styleSx: { display: { xs: 'flex', md: 'none' } }
                  }
              ]
            : []),

        {
            icon: <CampaignIcon sx={getIconStyle(ICON_COLORS.BLUE)} />,
            label: t('profile_menu.banner_ads', 'Banner ads'),
            onClick: () => navigateAndClose('/profile/banner-orders')
        },
        ...(canSeeAdminBannerManagement
            ? [
                  {
                      icon: <CampaignIcon sx={getIconStyle(ICON_COLORS.ORANGE)} />,
                      label: t('profile_menu.banner_moderation', 'Banner management'),
                      onClick: () => navigateAndClose('/admin/banner-management')
                  }
              ]
            : []),
        {
            icon: <ShoppingBagIcon sx={getIconStyle(ICON_COLORS.DARK_RED)} />,
            label: t('profile_menu.my_orders', 'My Orders'),
            onClick: () => navigateAndClose('/OrderHistory')
        },
        {
            icon: <LockResetIcon sx={getIconStyle(ICON_COLORS.GRAY)} />,
            label: t('app_settings.change_password', 'Change Password'),
            onClick: () => navigateAndClose('/ChangePassword')
        },
        {
            icon: <DeleteIcon sx={getIconStyle(ICON_COLORS.BLUE)} />,
            label: t('app_settings.delete_account', 'Delete Account'),
            onClick: () => setShowDeleteDialog(true)
        },
        {
            icon: <LogoutIcon sx={getIconStyle(ICON_COLORS.DARK_RED)} />,
            label: t('profile_menu.log_out', 'Log Out'),
            onClick: handleLogout
        }
    ];

    return (
        <>
            <List sx={{ minWidth: 240, width: '100%', bgcolor: 'background.default' }}>
                {menuItems.map((item) => (
                    <ProfileMenuItem key={item.label} {...item} />
                ))}
            </List>

            <DeleteAccountDialog open={showDeleteDialog} onClose={() => setShowDeleteDialog(false)} onConfirm={() => deleteAccount(userInfo?.email || '')} />
        </>
    );
};

export default ProfileMenu;
